'use client';

import { create } from 'zustand';
import { produce } from 'immer';
import { userApi, type UserProfile } from '@/lib/api/users';
import { useAuthStore } from '@/stores/authStore';
import { useUserProfileStore } from '@/stores/userProfileStore';
import {
  CREDITS_JOURNEY_KEYS, isLowCreditsBalance, lowCreditsThreshold,
  parseLowCreditsEpisode, type LowCreditsEpisode,
} from '@/lib/credits/journey';

interface JourneyQuote { projectId: string; credits: number | null; specKey: string }
interface CreditAdmissionFailure { userId: string; projectId: string; clientRequestId: string; requiredCredits?: number }
interface CreditsJourneyState {
  userId: string | null;
  ready: boolean;
  welcomeShown: boolean;
  welcomeRead: boolean;
  welcomeOwner: string | null;
  welcomeAutomatic: boolean;
  welcomeSaving: number;
  welcomeSaveError: boolean;
  lowEpisode: LowCreditsEpisode | null;
  lowSaving: number;
  lowSaveError: boolean;
  quote: JourneyQuote | null;
  submittedQuote: JourneyQuote | null;
  creditFailures: Record<string, CreditAdmissionFailure>;
  recordCreditFailure: (failure: CreditAdmissionFailure) => void;
  activateAccount: (userId: string | null) => void;
  hydrate: (profile: UserProfile) => void;
  openWelcome: (owner: string, automatic?: boolean) => Promise<void>;
  markWelcomeVisible: (owner: string, userId: string) => Promise<void>;
  hideWelcome: () => void;
  closeWelcomeAsRead: () => Promise<boolean>;
  setQuote: (quote: JourneyQuote | null) => void;
  rememberSubmissionQuote: (projectId: string, credits: number | null, specKey: string) => void;
  observeBalance: (balance: number) => Promise<void>;
  notifySettlement: (target: Omit<LowCreditsEpisode, 'recoveryThreshold' | 'dismissed'>, balance: number, quote: number | null) => Promise<void>;
  dismissLowEpisode: () => Promise<void>;
  retryLowSave: () => Promise<void>;
}

const initialState = {
  userId: null, ready: false, welcomeShown: false, welcomeRead: false,
  welcomeOwner: null, welcomeAutomatic: false, welcomeSaving: 0, welcomeSaveError: false,
  lowEpisode: null, lowSaving: 0, lowSaveError: false, quote: null, submittedQuote: null, creditFailures: {},
};

// Serialize writes so a late 'show' cannot overwrite a subsequent recovery/dismissal.
// Each job retains its account epoch; queued or completed work cannot leak across sign-ins.
let accountEpoch = 0;
let preferenceWrites: Promise<unknown> = Promise.resolve();

function persistPreferences(patch: Record<string, unknown>, scope: 'welcome' | 'low'): Promise<boolean> {
  const userId = useCreditsJourneyStore.getState().userId;
  const epoch = accountEpoch;
  const isCurrent = () => Boolean(userId && epoch === accountEpoch
    && useAuthStore.getState().user?.id === userId
    && useCreditsJourneyStore.getState().userId === userId);
  if (!isCurrent()) return Promise.resolve(false);
  const savingKey = scope === 'welcome' ? 'welcomeSaving' : 'lowSaving';
  const errorKey = scope === 'welcome' ? 'welcomeSaveError' : 'lowSaveError';
  useCreditsJourneyStore.setState((state) => ({ [savingKey]: state[savingKey] + 1, [errorKey]: false }));
  const job = preferenceWrites.then(async () => {
    try {
      if (!isCurrent()) return false;
      const response = await userApi.updatePreferences(patch);
      if (!isCurrent()) return false;
      const profile = response.data as UserProfile | undefined;
      // userApi normalizes success envelopes to data; success:false must still fail closed.
      if (response.success === false || response.error || profile?.user_id !== userId) {
        throw new Error('credits_journey_preference_save_failed');
      }
      // Merge only the keys this write owns, never the stale full GET response snapshot.
      useUserProfileStore.setState(produce((state) => {
        if (state.profile?.user_id === userId) Object.assign(state.profile.preferences, patch);
      }));
      return true;
    } catch {
      if (isCurrent()) useCreditsJourneyStore.setState({ [errorKey]: true });
      return false;
    } finally {
      if (isCurrent()) useCreditsJourneyStore.setState((state) => ({ [savingKey]: state[savingKey] - 1 }));
    }
  });
  preferenceWrites = job;
  return job;
}

export const useCreditsJourneyStore = create<CreditsJourneyState>((set, get) => ({
  ...initialState,
  activateAccount: (userId) => {
    if (get().userId === userId) return;
    accountEpoch += 1;
    set({ ...initialState, userId });
  },
  hydrate: (profile) => {
    if (profile.user_id !== useAuthStore.getState().user?.id) return;
    get().activateAccount(profile.user_id);
    if (get().ready) return;
    set({
      ready: true,
      welcomeShown: profile.preferences[CREDITS_JOURNEY_KEYS.shown] === true,
      welcomeRead: profile.preferences[CREDITS_JOURNEY_KEYS.read] === true,
      lowEpisode: parseLowCreditsEpisode(profile.preferences[CREDITS_JOURNEY_KEYS.lowEpisode]),
    });
  },
  openWelcome: async (owner, automatic = false) => {
    if (!get().ready || (automatic && (get().welcomeShown || get().welcomeRead || get().welcomeOwner))) return;
    set({ welcomeOwner: owner, welcomeAutomatic: automatic });
  },
  markWelcomeVisible: async (owner, userId) => {
    if (!get().ready || get().welcomeOwner !== owner || get().userId !== userId
      || useAuthStore.getState().user?.id !== userId) return;
    const needsShownSave = !get().welcomeShown || get().welcomeSaveError;
    set({ welcomeShown: true });
    if (needsShownSave) await persistPreferences({ [CREDITS_JOURNEY_KEYS.shown]: true }, 'welcome');
  },
  hideWelcome: () => set({ welcomeOwner: null }),
  closeWelcomeAsRead: async () => {
    const epoch = accountEpoch;
    const saved = await persistPreferences({
      [CREDITS_JOURNEY_KEYS.shown]: true, [CREDITS_JOURNEY_KEYS.read]: true,
    }, 'welcome');
    if (saved && epoch === accountEpoch) set({ welcomeShown: true, welcomeRead: true, welcomeOwner: null });
    return saved;
  },
  setQuote: (quote) => set({ quote }),
  rememberSubmissionQuote: (projectId, credits, specKey) => set({ submittedQuote: { projectId, credits, specKey } }),
  recordCreditFailure: (failure) => {
    if (failure.userId !== useAuthStore.getState().user?.id) return;
    get().activateAccount(failure.userId);
    set(produce((state: CreditsJourneyState) => {
      const previous = state.creditFailures[failure.clientRequestId];
      state.creditFailures[failure.clientRequestId] = { ...failure, requiredCredits: failure.requiredCredits ?? previous?.requiredCredits };
    }));
  },
  observeBalance: async (balance) => {
    if (!get().ready || !Number.isFinite(balance) || balance < 0) return;
    set(produce((state: CreditsJourneyState) => {
      for (const [id, failure] of Object.entries(state.creditFailures)) {
        if (failure.userId === state.userId && failure.requiredCredits
          && failure.requiredCredits > 0 && balance >= failure.requiredCredits) delete state.creditFailures[id];
      }
    }));
    const episode = get().lowEpisode;
    if (!episode || balance < episode.recoveryThreshold) return;
    set({ lowEpisode: null });
    await persistPreferences({ [CREDITS_JOURNEY_KEYS.lowEpisode]: null }, 'low');
  },
  notifySettlement: async (target, balance, quote) => {
    if (!get().ready || get().lowEpisode) return;
    const recoveryThreshold = lowCreditsThreshold(quote);
    if (!isLowCreditsBalance(balance, recoveryThreshold)) return;
    const episode = { ...target, recoveryThreshold, dismissed: false };
    set({ lowEpisode: episode });
    await persistPreferences({ [CREDITS_JOURNEY_KEYS.lowEpisode]: episode }, 'low');
  },
  dismissLowEpisode: async () => {
    if (!get().lowEpisode) return;
    set(produce((state: CreditsJourneyState) => { if (state.lowEpisode) state.lowEpisode.dismissed = true; }));
    await persistPreferences({ [CREDITS_JOURNEY_KEYS.lowEpisode]: get().lowEpisode }, 'low');
  },
  retryLowSave: async () => {
    await persistPreferences({ [CREDITS_JOURNEY_KEYS.lowEpisode]: get().lowEpisode }, 'low');
  },
}));
